import find from 'lodash/find';
import uniq from 'lodash/uniq';
import countries from 'constants/SupportedCountries.json';
import { getCountryFromEdge, getIp } from './cloudflare';
export const DEFAULT_COUNTRY = find(countries, { default: true });
export const SUPPORTED_LOCALES = uniq(countries.map((country) => country.locale));
export const COOKIE_NAME = 'country';
let cookie;
if (process.browser) {
    cookie = require('cookies-js');
}
function findCountry(country) {
    if (!country) {
        return null;
    }
    const countryCode = country.toLowerCase() === 'uk' ? 'gb' : country.toLowerCase();
    return find(countries, { country: countryCode }) || null;
}
function ensureCountry(country) {
    return findCountry(country) || DEFAULT_COUNTRY;
}
// Trimmed, with '0' treated as no value, to match how the ALB decides whether to
// skip its cache: it strips the space after '=' and counts '0' as unset. A cookie
// we honour but it ignores gets that visitor the geo-keyed page.
function cookieChoice(rawValue) {
    const value = (rawValue || '').trim();
    return value && value !== '0' ? value : null;
}
function asResolved(rawCountry, source) {
    if (!rawCountry) {
        return null;
    }
    const detected = rawCountry.toLowerCase();
    const country = findCountry(rawCountry);
    // `source` survives an unrecognised value on purpose: it decides whether the
    // render may be shared, so a `country=zz` cookie must stay 'choice'.
    return { country: country ? country.country : DEFAULT_COUNTRY.country, source, detected };
}
export function clearCountry(request) {
    if (process.browser) {
        cookie.set(COOKIE_NAME);
    }
    else {
        request.cookies.set(COOKIE_NAME);
    }
}
// Ten years, so only for a country the visitor asked for — never a detected one,
// which would pin a guess (or one VPN session) for a decade with no way back.
export function setCountry(country, request) {
    const supportedCountry = ensureCountry(country);
    const expires = new Date();
    expires.setTime(expires.valueOf() + 10 * 365 * 24 * 60 * 60 * 1000);
    if (process.browser) {
        cookie.set(COOKIE_NAME, supportedCountry.country, { expires: expires, path: '/' });
    }
    else {
        request.cookies.set(COOKIE_NAME, supportedCountry.country, { expires, path: '/', httpOnly: false, secure: false });
    }
}
export function resolveCountry(request) {
    var _a;
    if (process.browser) {
        return (asResolved(cookieChoice(cookie.get(COOKIE_NAME)), 'choice') || {
            country: DEFAULT_COUNTRY.country,
            source: 'default',
            detected: null,
        });
    }
    const chosen = asResolved(cookieChoice(request.cookies.get(COOKIE_NAME)), 'choice');
    if (chosen) {
        return chosen;
    }
    const edge = asResolved(getCountryFromEdge(request), 'edge');
    if (edge) {
        return edge;
    }
    // node-fetch is us calling ourselves: an internal hop's location means nothing.
    const isNodeFetch = (_a = request.header['user-agent']) === null || _a === void 0 ? void 0 : _a.includes('node-fetch');
    if (!isNodeFetch) {
        const geoip = require('geoip-lite');
        const lookup = geoip.lookup(getIp(request));
        const local = asResolved(lookup && lookup.country, 'geoip');
        if (local) {
            return local;
        }
    }
    return { country: DEFAULT_COUNTRY.country, source: 'default', detected: null };
}
export function getLocaleByCountry(country) {
    return ensureCountry(country).locale;
}
